<template>
  <span>
    <slot name="button" v-bind="{ onclick: toggle }">
      <b-button variant="info" @click="toggle"><i class="fas fa-copy"></i> Copy exceptions</b-button>
    </slot>

    <b-modal ref="modal-container" v-model="modalShow" size="lg" :static="true" hide-footer
      title="Copy exceptions" @show="onShow" @hidden="onHidden">

      <h6 class="text-muted">Source</h6>
      <b-form-group label="Country" label-for="copy-source-country-input">
        <CountryCodeSelect id="copy-source-country-input" v-model="sourceCountryCode" />
      </b-form-group>

      <b-form-group label="Operator" label-for="copy-source-operator-input">
        <multiselect id="copy-source-operator-input" v-model="sourceOperator" :options="operatorOptions"
          label="label" track-by="value" :loading="!operatorsLoaded" :multiple="false"
          placeholder="Select operator" class="operator-select" @input="onSourceChanged" />
      </b-form-group>

      <p v-if="sourceLoading" class="text-muted small">
        <i class="fas fa-spinner fa-spin"></i> Loading source exceptions...
      </p>
      <p v-else-if="sourceNormalizedCode && sourceOperator" class="text-muted small mb-0">
        <span v-if="hasSourceExceptions">{{ sourceSummary }}</span>
        <span v-else class="text-warning">No exceptions found for this country and operator.</span>
      </p>

      <hr class="my-3" />

      <h6 class="text-muted">Target</h6>
      <b-form-group label="Country" label-for="copy-target-country-input">
        <CountryCodeSelect id="copy-target-country-input" v-model="targetCountryCode" />
      </b-form-group>

      <b-form-group label="Operator" label-for="copy-target-operator-input">
        <multiselect id="copy-target-operator-input" v-model="targetOperator" :options="operatorOptions"
          label="label" track-by="value" :loading="!operatorsLoaded" :multiple="false"
          placeholder="Select operator" class="operator-select" />
      </b-form-group>

      <b-alert v-if="targetNormalizedCode && targetCountryBlocked === false" show variant="danger" class="mt-3 mb-0">
        Country <b>{{ targetNormalizedCode }}</b> is not blocked. Block the country first before copying exceptions.
      </b-alert>

      <div v-if="targetNormalizedCode && targetBlockChecking" class="text-muted mt-3">
        <i class="fas fa-spinner fa-spin"></i> Checking target country block status...
      </div>

      <b-alert v-if="isSameSelection" show variant="warning" class="mt-3 mb-0">
        Source and target must be different.
      </b-alert>

      <div class="mt-3 text-right">
        <b-button variant="primary" class="mr-2" :disabled="!canCopy || copying" @click="copy">
          <i class="fas fa-copy"></i> Copy
        </b-button>
        <b-button variant="secondary" @click="modalShow = false">Close</b-button>
      </div>
    </b-modal>
  </span>
</template>

<script>
import Multiselect from "vue-multiselect";
import CountryCodeSelect from "./CountryCodeSelect.vue";
import {
  copyExceptionsToTarget,
  fetchCountryExceptions,
  formatExceptionSummary,
  hasExceptionItems,
  isCountryBlocked,
} from "./exceptionUtils";

export default {
  name: "ExceptionsCopyForm",
  components: { Multiselect, CountryCodeSelect },
  data() {
    return {
      modalShow: false,
      operatorsLoaded: false,
      operatorOptions: [],
      sourceCountryCode: '',
      sourceOperator: null,
      sourceExceptions: null,
      sourceLoading: false,
      targetCountryCode: '',
      targetOperator: null,
      targetCountryBlocked: null,
      targetBlockChecking: false,
      copying: false,
    };
  },
  computed: {
    sourceNormalizedCode() {
      const c = (this.sourceCountryCode || '').trim().toUpperCase();
      return c === '' ? null : c;
    },
    targetNormalizedCode() {
      const c = (this.targetCountryCode || '').trim().toUpperCase();
      return c === '' ? null : c;
    },
    hasSourceExceptions() {
      return hasExceptionItems(this.sourceExceptions);
    },
    sourceSummary() {
      return formatExceptionSummary(this.sourceExceptions);
    },
    isSameSelection() {
      if (!this.sourceNormalizedCode || !this.targetNormalizedCode) return false;
      if (!this.sourceOperator?.value || !this.targetOperator?.value) return false;
      return this.sourceNormalizedCode === this.targetNormalizedCode
        && this.sourceOperator.value === this.targetOperator.value;
    },
    canCopy() {
      return this.hasSourceExceptions
        && this.targetNormalizedCode
        && this.targetOperator?.value
        && this.targetCountryBlocked === true
        && !this.isSameSelection
        && !this.copying
        && !this.sourceLoading
        && !this.targetBlockChecking;
    },
  },
  watch: {
    sourceCountryCode() {
      this.onSourceChanged();
    },
    targetCountryCode() {
      this.onTargetCountryChanged();
    },
  },
  methods: {
    toggle() {
      this.modalShow = !this.modalShow;
    },
    onHidden() {
      this.sourceCountryCode = '';
      this.sourceOperator = null;
      this.sourceExceptions = null;
      this.targetCountryCode = '';
      this.targetOperator = null;
      this.targetCountryBlocked = null;
      this.sourceLoading = false;
      this.targetBlockChecking = false;
      this.copying = false;
    },
    async onShow() {
      await this.loadOperators();
    },
    async loadOperators() {
      try {
        const resp = await this.$http({ url: '/api/v1/country-partners-for-select', method: 'GET' });
        this.operatorOptions = resp.data;
        this.operatorsLoaded = true;
      } catch (err) {
        this.$toastr.e('Failed to load operators');
      }
    },
    async onSourceChanged() {
      this.sourceExceptions = null;

      if (!this.sourceNormalizedCode || !this.sourceOperator?.value) {
        return;
      }

      this.sourceLoading = true;
      try {
        this.sourceExceptions = await fetchCountryExceptions(
          (config) => this.$http(config),
          this.sourceNormalizedCode,
          this.sourceOperator.value,
        );
      } catch (err) {
        this.$toastr.e('Failed to load source exceptions');
      } finally {
        this.sourceLoading = false;
      }
    },
    async onTargetCountryChanged() {
      this.targetCountryBlocked = null;

      if (!this.targetNormalizedCode) {
        return;
      }

      this.targetBlockChecking = true;
      try {
        this.targetCountryBlocked = await isCountryBlocked(
          (config) => this.$http(config),
          this.targetNormalizedCode,
        );
      } catch (err) {
        this.targetCountryBlocked = false;
        this.$toastr.e('Failed to check target country block status');
      } finally {
        this.targetBlockChecking = false;
      }
    },
    async copy() {
      if (!this.canCopy) return;

      this.copying = true;
      try {
        const results = await copyExceptionsToTarget(
          (config) => this.$http(config),
          this.sourceExceptions,
          this.targetNormalizedCode,
          this.targetOperator.value,
        );

        let message = 'Exceptions copied';
        if (results.skipped > 0) {
          message += ` (${results.created} created, ${results.skipped} already existed)`;
        }
        this.$toastr.i(message);
        this.$emit('finish');
        this.modalShow = false;
      } catch (err) {
        const message = err.response?.data?.message ?? err.message;
        this.$toastr.e(typeof message === 'string' ? message : JSON.stringify(message));
      } finally {
        this.copying = false;
      }
    },
  },
};
</script>

<style scoped>
.operator-select {
  min-width: 240px;
}
</style>
